import { useNavigate } from 'react-router-dom';
import { articles } from '../../../mocks/articles';

/** 新着記事：アップ日から1週間以内の記事を表示 */
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

function parseJapaneseDate(dateStr: string): Date | null {
  // "2026年4月3日" → Date
  const m = dateStr.match(/(\d{4})年(\d{1,2})月(\d{1,2})日/);
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

function getNewArticles() {
  const now = new Date();
  const recent = articles
    .map((a) => ({ ...a, parsedDate: parseJapaneseDate(a.date) }))
    .filter((a) => a.parsedDate && now.getTime() - a.parsedDate.getTime() <= ONE_WEEK_MS)
    .sort((a, b) => (b.parsedDate?.getTime() ?? 0) - (a.parsedDate?.getTime() ?? 0))
    .slice(0, 5);
  return recent;
}

/** カテゴリカラー */
const CATEGORY_COLOR: Record<string, { bg: string; text: string }> = {
  '知識をつける':         { bg: 'bg-[#EBF5FF]', text: 'text-[#3A70B0]' },
  '予算を決める':         { bg: 'bg-[#FFF4E8]', text: 'text-[#B07030]' },
  'パートナーを選ぶ':     { bg: 'bg-[#EAF7EE]', text: 'text-[#387A50]' },
  '物件・プランを決める': { bg: 'bg-[#FFF0EB]', text: 'text-[#B05030]' },
  '契約・引き渡し':       { bg: 'bg-[#FFF0F0]', text: 'text-[#A03040]' },
};

export default function NewArticlesSection() {
  const navigate = useNavigate();
  const newArticles = getNewArticles();

  if (newArticles.length === 0) return null;

  return (
    <section
      id="new-articles"
      aria-labelledby="new-articles-heading"
      className="py-14 md:py-20 bg-white"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-3 mb-8">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#FFF0E8] text-[#C07030] text-xs font-semibold tracking-wide mb-3">
              <i className="ri-sparkling-2-fill" aria-hidden="true" />
              新着記事（1週間以内）
            </div>
            <h2
              id="new-articles-heading"
              className="text-2xl md:text-3xl font-bold text-[#2C2C2C] leading-tight"
            >
              最新の記事をチェック
            </h2>
            <p className="text-[#6B6B6B] text-sm mt-1.5">
              公開から1週間以内の新着情報をお届けします
            </p>
          </div>
        </div>

        {/* Article List */}
        <div className="flex flex-col gap-4">
          {newArticles.map((article, idx) => {
            const colors = CATEGORY_COLOR[article.category] ?? { bg: 'bg-[#E8F0E6]', text: 'text-[#5A7A52]' };
            return (
              <a
                key={article.id}
                href={`/articles/${article.id}`}
                onClick={(e) => { e.preventDefault(); navigate(`/articles/${article.id}`); }}
                className="group flex flex-col sm:flex-row gap-4 bg-[#FAFAF8] rounded-2xl overflow-hidden border border-[#EDEAE6] hover:border-[#C8D8C0] transition-all duration-200 hover:-translate-y-0.5 cursor-pointer no-underline"
                aria-label={`新着記事「${article.title}」を読む`}
              >
                {/* Thumbnail */}
                <div className="relative w-full sm:w-48 md:w-56 flex-shrink-0 h-40 sm:h-auto bg-[#EDEAE6] overflow-hidden rounded-t-2xl sm:rounded-l-2xl sm:rounded-tr-none">
                  {/* NEW badge */}
                  <div className="absolute top-2.5 left-2.5 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#C07030] text-white text-[10px] font-bold">
                    <i className="ri-sparkling-2-fill text-xs" aria-hidden="true" />
                    NEW
                  </div>
                  {/* Rank number */}
                  {idx === 0 && (
                    <div className="absolute top-2.5 right-2.5 z-10 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/90 text-[#C07030] text-[10px] font-bold border border-[#F0D0B0]">
                      最新
                    </div>
                  )}
                  <img
                    src={article.image}
                    alt={article.title}
                    loading="lazy"
                    decoding="async"
                    width="224"
                    height="160"
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>

                {/* Body */}
                <div className="flex flex-col justify-center p-4 md:p-5 flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${colors.bg} ${colors.text}`}>
                      {article.category}
                    </span>
                    <span className="text-[#A0A0A0] text-[10px] flex items-center gap-1">
                      <i className="ri-calendar-line text-xs" aria-hidden="true" />
                      {article.date}
                    </span>
                    <span className="text-[#A0A0A0] text-[10px] flex items-center gap-0.5">
                      <i className="ri-time-line text-xs" aria-hidden="true" />
                      {article.readTime}
                    </span>
                  </div>
                  <h3 className="text-[#2C2C2C] text-sm md:text-base font-bold leading-snug line-clamp-2 mb-2 group-hover:text-[#8FA882] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-[#6B6B6B] text-xs leading-relaxed line-clamp-2 mb-3 hidden sm:block">
                    {article.excerpt}
                  </p>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {article.tags.slice(0, 3).map((tag) => (
                      <span key={tag} className="px-2 py-0.5 rounded-md bg-[#F5F1ED] text-[#8A7040] text-[10px]">
                        #{tag}
                      </span>
                    ))}
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-[#8FA882] text-xs font-semibold group-hover:gap-2.5 transition-all duration-200 whitespace-nowrap">
                    続きを読む
                    <i className="ri-arrow-right-line" aria-hidden="true" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
